import { get, post, put, del } from "@/shared/utils/fetchApi";
import type {
  EmailTemplateBase,
  EmailTemplate,
  UpdateEmailTemplatePayload,
  CreateEmailTemplatePayload,
  AvailableTemplate,
} from "../types/EmailTemplateTypes";

export class EmailTemplateService {
  // Base templates
  static async getBaseTemplates(): Promise<EmailTemplateBase[]> {
    // v2: `GET /email-templates/base` (superadmin) array flat. Legacy: array flat tb.

    return (await get<EmailTemplateBase[]>("/email-templates/base")) ?? [];
  }

  static async getBaseTemplate(
    slug: string,
    lang: string,
  ): Promise<EmailTemplateBase> {
    return get<EmailTemplateBase>(`/email-templates/base/${slug}/${lang}`);
  }

  static async updateBaseTemplate(
    slug: string,
    lang: string,
    payload: UpdateEmailTemplatePayload,
  ): Promise<EmailTemplateBase> {
    return put<EmailTemplateBase>(
      `/email-templates/base/${slug}/${lang}`,
      payload,
    );
  }

  static async revertOrgTemplateToBase(
    slug: string,
    lang: string,
  ): Promise<EmailTemplate> {
    return post<EmailTemplate>(`/email-templates/${slug}/${lang}/revert`, {});
  }

  // Org templates
  static async getOrgTemplates(): Promise<EmailTemplate[]> {
    return (await get<EmailTemplate[]>("/email-templates")) ?? [];
  }

  static async getOrgTemplate(
    slug: string,
    lang: string,
  ): Promise<EmailTemplate> {
    return get<EmailTemplate>(`/email-templates/${slug}/${lang}`);
  }

  static async createOrgTemplate(
    payload: CreateEmailTemplatePayload,
  ): Promise<EmailTemplate> {
    return post<EmailTemplate>("/email-templates", payload);
  }

  static async updateOrgTemplate(
    slug: string,
    lang: string,
    payload: UpdateEmailTemplatePayload,
  ): Promise<EmailTemplate> {
    return put<EmailTemplate>(`/email-templates/${slug}/${lang}`, payload);
  }

  static async deleteOrgTemplate(slug: string, lang: string): Promise<void> {
    // v2: 204 No Content. Legacy: 200 {success,message} (ignorado).

    return del<void>(`/email-templates/${slug}/${lang}`);
  }

  // Available templates (for workflow node selectors)
  static async getAvailableTemplates(
    language?: string,
  ): Promise<AvailableTemplate[]> {
    const params = language ? `?language=${language}` : "";
    // v2: array flat (divergência D1; legacy era {success,data}).

    return (
      (await get<AvailableTemplate[]>(
        `/email-templates/available${params}`,
      )) ?? []
    );
  }

  // Preview
  static async previewTemplate(
    content: string,
    variables: Record<string, string>,
  ): Promise<{ rendered: string }> {
    return post<{ rendered: string }>("/email-templates/preview", {
      content,
      variables,
    });
  }
}
